import { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { isSupportedTagType } from '@/entities/Tag/config/tag-types.config';
import { clientService } from '@/entities/Client/api/clientService';
import { executorService } from '@/entities/Executor/api/executorService';
import { orderService } from '@/entities/Order/api/orderService';
import { isSubscriptionPurchase } from '@/entities/Order/lib/isSubscriptionPurchase';
import { orderTaskService } from '@/entities/OrderTask/api/orderTaskService';
import { bonusAccountService } from '@/entities/BonusAccount/api/bonusAccountService';
import { BonusOperationKind } from '@/entities/BonusAccount/model/types';
import { promoCodeService } from '@/entities/PromoCode/api/promoCodeService';
import { productService } from '@/entities/Product/api/productService';
import { priceListService } from '@/entities/PriceList/api/priceListService';
import { policyService } from '@/entities/Policy/api/policyService';
import { blogService } from '@/entities/Blog/api/blogService';
import { orderProblemService } from '@/entities/OrderProblem/api/orderProblemService';
import { useOrderProblems } from '@/entities/OrderProblem/model/store';
import { operatorTaskService } from '@/entities/OperatorTask/api/operatorTaskService';
import { problemAddressService } from '@/entities/ProblemAddress/api/problemAddressService';
import { dialogService } from '@/entities/UserDialog/api/dialogService';
import { useTaskCompletion } from '@/features/operatorTasks/model/useTaskCompletion';
import { orderToTaskOrigin } from '@/features/operatorTasks/model/origins';
import { canAccessPermission, useAdminAccess } from '@/shared/model/adminAccess';
import { showConfirmDialog } from '@/shared/ui/ConfirmDialogWithComment/confirmDialogState';
import type { ActionMenuItem } from '@/shared/ui/ActionMenu/types';
import { RequestError } from '@/shared/ui/RequestState';
import { RecordActionDialogs } from '../ui/RecordActionDialogs';
import { getRecordActionItems } from './recordActionItems';
import { useStartOrderByAdmin } from './useStartOrderByAdmin';
import { getRecordKey, getRelatedRecord } from './recordTarget';
import type {
    RecordActionCommands,
    RecordActionDialog,
    RecordActionKey,
    RecordKind,
} from './types';

/** Открывает действия записи непосредственно из списка */
export const useRecordActions = (kind: RecordKind, onChanged?: () => void | Promise<void>) => {
    const navigate = useNavigate();
    const [dialog, setDialog] = useState<RecordActionDialog | null>(null);
    const pending = useRef(new Set<string>());
    const changed = useRef(onChanged);
    changed.current = onChanged;
    const completion = useTaskCompletion(kind);

    /** Обновляет текущую страницу после действия */
    const refresh = useCallback(async (): Promise<void> => { await changed.current?.(); }, []);
    const startOrderByAdmin = useStartOrderByAdmin(refresh);

    /** Возвращает действия выбранной строки */
    const getMenuItems = async (row: object): Promise<ActionMenuItem[]> => {
        const id = kind === 'bonusAccounts'
            ? getRecordKey(getRelatedRecord(row, 'client'))
            : kind === 'scheduler'
                ? getRecordKey(row, 'jobKey')
                : kind === 'tags'
                    ? getRecordKey(getRelatedRecord(row, 'type'))
                    : getRecordKey(row);

        if (!id || (kind === 'tags' && !isSupportedTagType(id))) return [];

        const encoded = encodeURIComponent(id);

        const routes = {
            appVersions: '/app-versions',
            notificationTemplates: '/notification-templates',
            promotionBanners: '/promotion-banner-templates',
            subscriptions: '/subscriptions',
            clientSubscriptions: '/client-subscriptions',
            pets: '/pets',
            tags: '/tags',
            cashbackLevels: '/cashback-levels',
            messageTemplates: '/message-templates',
            scheduler: '/scheduler',
            products: '/products',
            priceLists: '/price-lists',
            policies: '/policies',
            blogs: '/blogs',
            orders: '/orders',
            clients: '/clients',
            executors: '/executors',
            promoCodes: '/promo-codes',
            dialogTemplates: '/user-dialogs/templates',
        };

        const commands: RecordActionCommands = {};

        if (kind in routes) {
            const base = Reflect.get(routes, kind) as string;
            commands.edit = () => navigate(kind === 'dialogTemplates'
                ? `${base}/${encoded}`
                : `${base}/${encoded}/edit`);
        }

        /** Подтверждает изменение выбранной записи */
        const confirm = (
            header: string,
            message: string,
            acceptLabel: string,
            action: (comment?: string) => Promise<unknown>,
            showCommentField = true,
        ): void => {
            const key = `${kind}:${id}`;

            showConfirmDialog({
                header,
                message,
                acceptLabel,
                commentPlaceholder: 'Укажите причину',
                showCommentField,
                onConfirm: async (comment) => {
                    if (pending.current.has(key)) return;

                    pending.current.add(key);

                    try {
                        await action(comment);
                        toast.success('Действие выполнено');
                        await refresh();
                    } finally {
                        pending.current.delete(key);
                    }
                },
            });
        };

        if (kind === 'clients') {
            const record = await clientService.getById(id);
            commands.createOrder = () => navigate(`/orders/new?from=client&clientId=${encoded}`);
            commands.chat = () => { window.open(`/chats?app_user_id=${record.appUserId ?? ''}&role=Client`, '_blank', 'noopener,noreferrer'); };
            commands.email = () => setDialog({
                kind: 'clientEmail',
                record
            });
            commands.transferPhone = () => setDialog({
                kind: 'profilePhone',
                target: {
                    kind: 'client',
                    profileId: id
                },
                currentPhone: record.phone,
            });
            commands.block = () => confirm('Блокировка профиля клиента', 'Вы уверены, что хотите заблокировать профиль клиента?', 'Заблокировать', comment => clientService.block(id, { reason: comment ?? '' }));
            commands.unblock = () => confirm('Разблокировка профиля клиента', 'Вы уверены, что хотите разблокировать профиль клиента?', 'Разблокировать', comment => clientService.unblock(id, { reason: comment ?? '' }));
            commands.deactivate = () => confirm('Деактивация профиля клиента', 'Вы уверены, что хотите деактивировать профиль клиента?', 'Деактивировать', comment => clientService.deactivate(id, { comment }));

            return getRecordActionItems({
                kind,
                record
            }, commands, pending.current.has(`${kind}:${id}`));
        }

        if (kind === 'executors') {
            const record = await executorService.getById(id);
            commands.chat = () => { window.open(`/chats?app_user_id=${record.appUserId ?? ''}&role=Executor`, '_blank', 'noopener,noreferrer'); };
            commands.email = () => setDialog({
                kind: 'executorEmail',
                record
            });
            commands.transferPhone = () => setDialog({
                kind: 'profilePhone',
                target: {
                    kind: 'executor',
                    profileId: id
                },
                currentPhone: record.phone,
            });
            commands.assignPenalty = () => confirm('Назначить штраф', 'Назначить штраф этому исполнителю?', 'Назначить штраф', () => executorService.assignPenalty(id), false);
            commands.removePenalty = () => confirm('Снять штраф', 'Снять штраф с этого исполнителя?', 'Снять штраф', () => executorService.removePenalty(id), false);
            commands.passport = () => navigate(`/executors/${encoded}/passport`);
            commands.verify = () => confirm('Подтверждение верификации', 'Вы уверены, что хотите верифицировать этого исполнителя?', 'Верифицировать', () => executorService.verify(id), false);
            commands.block = () => confirm('Блокировка профиля исполнителя', 'Вы уверены, что хотите заблокировать профиль исполнителя?', 'Заблокировать', comment => executorService.block(id, comment ?? ''));
            commands.unblock = () => confirm('Разблокировка профиля исполнителя', 'Вы уверены, что хотите разблокировать профиль исполнителя?', 'Разблокировать', comment => executorService.unblock(id, comment ?? ''));
            commands.deactivate = () => confirm('Деактивация профиля исполнителя', 'Вы уверены, что хотите деактивировать профиль исполнителя?', 'Деактивировать', comment => executorService.deactivate(id, comment));

            return getRecordActionItems({
                kind,
                record
            }, commands, pending.current.has(`${kind}:${id}`));
        }

        if (kind === 'orders') {
            const record = await orderService.getOrderById(id);
            const snapshot = useAdminAccess.getState().snapshot;

            const task = !isSubscriptionPurchase(record) && record.executorId
                && canAccessPermission(snapshot, 'admin.orders.tasks.view')
                ? await orderTaskService.getByOrderId(id)
                : null;

            const modalKeys: RecordActionKey[] = ['complete', 'confirmPayment', 'startSearch', 'approveExecutor', 'removeExecutor', 'payTask', 'assignExecutor', 'createPayment'];

            for (const action of modalKeys) {
                commands[action] = () => setDialog({
                    kind: 'order',
                    record,
                    task,
                    action
                });
            }

            commands.startExecution = () => startOrderByAdmin(id);
            commands.cancel = () => navigate(`/orders/${encoded}/cancel`);
            commands.repeat = async () => {
                if (pending.current.has(`orders:${id}`)) return;

                pending.current.add(`orders:${id}`);

                try {
                    const created = await orderService.repeatOrder(id);

                    if (!created.id) throw new Error('Не удалось получить идентификатор заказа');

                    toast.success('Заказ успешно повторён');
                    window.open(`/orders/${encodeURIComponent(created.id)}`, '_blank', 'noopener,noreferrer');
                    await refresh();
                } finally {
                    pending.current.delete(`orders:${id}`);
                }
            };

            const origin = orderToTaskOrigin(record);
            commands.createTask = () => {
                if (origin) setDialog({
                    kind: 'task',
                    creating: true,
                    origin
                });
            };

            return getRecordActionItems({
                kind,
                record,
                task,
                canCreateTask: Boolean(origin) && canAccessPermission(snapshot, 'admin.operator-tasks.view')
            }, commands);
        }

        if (kind === 'bonusAccounts') {
            const record = await bonusAccountService.getByClientId(id);
            commands.accrue = () => setDialog({
                kind: 'bonus',
                clientId: id,
                operation: BonusOperationKind.Accrue
            });
            commands.spend = () => setDialog({
                kind: 'bonus',
                clientId: id,
                operation: BonusOperationKind.Spend
            });
            commands.block = () => confirm('Блокировка бонусного счёта', 'Вы уверены, что хотите заблокировать бонусный счёт?', 'Заблокировать', comment => bonusAccountService.block(id, { comment: comment?.trim() || null }));
            commands.unblock = () => confirm('Разблокировка бонусного счёта', 'Вы уверены, что хотите разблокировать бонусный счёт?', 'Разблокировать', comment => bonusAccountService.unblock(id, { comment: comment?.trim() || null }));

            return getRecordActionItems({
                kind,
                record
            }, commands);
        }

        if (kind === 'promoCodes') {
            const record = await promoCodeService.getById(id);
            commands.activate = () => confirm('Активация промокода', 'Вы уверены, что хотите активировать этот промокод?', 'Активировать', () => promoCodeService.activate(id), false);
            commands.block = () => confirm('Блокировка промокода', 'Вы уверены, что хотите заблокировать этот промокод?', 'Заблокировать', () => promoCodeService.block(id));

            return getRecordActionItems({
                kind,
                record
            }, commands, pending.current.has(`${kind}:${id}`));
        }

        if (kind === 'orderProblems') {
            const details = await orderProblemService.getDetails(id);

            if (!details.problem) return [];

            commands.retry = async () => { if (await useOrderProblems.getState().retry(id)) await refresh(); };
            commands.resolve = async () => { if (await useOrderProblems.getState().resolve(id)) await refresh(); };

            return getRecordActionItems({
                kind,
                record: details.problem
            }, commands);
        }

        if (kind === 'operatorTasks') {
            const record = await operatorTaskService.details(id);
            commands.edit = () => setDialog({
                kind: 'task',
                id,
                editing: true
            });
            commands.complete = () => completion.changeStatus(record);
            commands.reopen = () => completion.changeStatus(record, true);

            return getRecordActionItems({
                kind,
                record
            }, commands, completion.pending.includes(id));
        }

        if (kind === 'problemAddresses') {
            const record = await problemAddressService.getDetails(id);
            commands.verifyAddress = () => setDialog({
                kind: 'address',
                record
            });
        }

        if (kind === 'dialogBroadcasts') {
            const details = await dialogService.getDetails(id, {
                skipCount: 0,
                takeCount: 1
            });

            commands.refresh = refresh;

            if (details.broadcast?.templateId) {
                const templateId = details.broadcast.templateId;
                commands.openTemplate = () => navigate(`/user-dialogs/templates/${encodeURIComponent(templateId)}`);
            }
        }

        if (kind === 'products') commands.audit = () => setDialog({
            kind: 'audit',
            productId: id
        });
        if (kind === 'products') commands.deactivate = () => confirm('Деактивация продукта', 'Вы уверены, что хотите деактивировать этот продукт?', 'Деактивировать', comment => productService.deactivate(id, comment));
        if (kind === 'priceLists') commands.deactivate = () => confirm('Деактивация прайс-листа', 'Вы уверены, что хотите деактивировать этот прайс-лист?', 'Деактивировать', comment => priceListService.deactivate(id, comment));
        if (kind === 'policies') commands.deactivate = () => confirm('Деактивация политики', 'Вы уверены, что хотите деактивировать эту политику?', 'Деактивировать', comment => policyService.deactivate(id, comment?.trim()));
        if (kind === 'blogs') commands.deactivate = () => confirm('Деактивация блога', 'Вы уверены, что хотите деактивировать этот блог?', 'Деактивировать', () => blogService.deactivate(id), false);

        return getRecordActionItems({ kind }, commands);
    };

    const dialogs = <>
        {completion.error &&
            <RequestError
                compact
                message={completion.error}
            />
        }
        <RecordActionDialogs
            dialog={dialog}
            onClose={() => setDialog(null)}
            onChanged={refresh}
            onTaskOpen={(id, editing) => setDialog({
                kind: 'task',
                id,
                editing
            })}
        />
    </>;

    return {
        getMenuItems,
        dialogs,
        error: completion.error
    };
};
